import React, { createContext, useContext, useEffect, useState, useCallback } from 'react';

// Mock auth backed by localStorage. The `user` shape intentionally matches what
// Supabase Auth's session.user looks like, so swapping to real auth later is a
// rewrite of this file only — components that destructure `user` don't change.
//
// Future swap: replace state init + signIn/signOut with supabase.auth wrappers,
// and populate id/email from the real session. Keep the same exported names.

const STORAGE_KEY = 'quiver_user';

const AuthContext = createContext({
  user: null,
  signIn: () => {},
  signOut: () => {},
});

const readStoredUser = () => {
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    if (!raw) return null;
    const parsed = JSON.parse(raw);
    if (!parsed || typeof parsed.name !== 'string') return null;
    return parsed;
  } catch {
    return null;
  }
};

export function AuthProvider({ children }) {
  const [user, setUser] = useState(() => readStoredUser());

  // Sync across tabs.
  useEffect(() => {
    const onStorage = (e) => {
      if (e.key === STORAGE_KEY) setUser(readStoredUser());
    };
    window.addEventListener('storage', onStorage);
    return () => window.removeEventListener('storage', onStorage);
  }, []);

  const signIn = useCallback((name) => {
    const trimmed = (name || '').trim();
    if (trimmed.length < 2 || trimmed.length > 40) return;
    const next = {
      id: null,
      email: null,
      name: trimmed,
      isMock: true,
    };
    localStorage.setItem(STORAGE_KEY, JSON.stringify(next));
    setUser(next);
  }, []);

  const signOut = useCallback(() => {
    localStorage.removeItem(STORAGE_KEY);
    setUser(null);
  }, []);

  return (
    <AuthContext.Provider value={{ user, signIn, signOut }}>
      {children}
    </AuthContext.Provider>
  );
}

export const useAuth = () => useContext(AuthContext);
